import { PhoneReport, PhoneReportByTestIdInfo, PhoneReportOutcomeResolutionEnum, PhoneReportTestType } from '../models/phone-report.model';
import { TestStatusEnum } from '../models/test-group.model';
import { TestResultAction } from '../models/test-result.model';
import { AuthUser, Role } from '../models/user.model';
import { CustomUtils } from './custom-utils';

export class PhoneReportUtils {
  private static phoneReportBlockedStatuses = new Set([TestStatusEnum.TEST_NUMBER_NOT_AVAILABLE, TestStatusEnum.TEST_NUMBER_OFFLINE]);

  static getPhoneReportState({
    test,
    testType,
    phoneReportsInfo,
    currentUser,
    isPhoneReportsLoading,
  }: {
    test: TestResultAction;
    phoneReportsInfo: PhoneReportByTestIdInfo[];
    currentUser: AuthUser;
    isPhoneReportsLoading: boolean;
    testType: PhoneReportTestType;
  }) {
    const testHasPhone = !!test.destinationDto?.phone;
    const isAlreadyReported = phoneReportsInfo.findIndex(info => info.testId === test.id && info.testType === testType) !== -1;
    const isAdmin = currentUser.role === Role.ADMIN;
    const canAdminCreateReport = test.userId === currentUser.id;

    const canCreateReport = PhoneReportUtils.canCreatePhoneReport(
      testHasPhone,
      test.createdAt,
      isAlreadyReported,
      isAdmin,
      canAdminCreateReport,
      test.testStatus
    );
    const createReportTooltip = PhoneReportUtils.getPhoneReportCreationTooltip(
      test.createdAt,
      isAlreadyReported,
      isAdmin,
      canAdminCreateReport,
      test.testStatus
    );

    return {
      isAllowed: !isPhoneReportsLoading && canCreateReport,
      tooltip: createReportTooltip,
      canOpenReport: !isPhoneReportsLoading && isAlreadyReported,
    };
  }

  static canCreatePhoneReport(
    testHasPhone: boolean,
    createdAt: string | Date,
    isAlreadyReported: boolean,
    isAdmin: boolean,
    canAdminCreateReport: boolean,
    status: TestStatusEnum | null
  ): boolean {
    if (isAlreadyReported || (isAdmin && !canAdminCreateReport)) {
      return false;
    }

    const isTestCreatedWithinHours = CustomUtils.isTestWithinHours(createdAt, 24);
    const isBlocked = this.isPhoneReportBlockedByStatus(status);

    return testHasPhone && isTestCreatedWithinHours && !isBlocked;
  }

  static isPhoneReportBlockedByStatus(status?: TestStatusEnum | null): boolean {
    if (!status) {
      return false;
    }
    return this.phoneReportBlockedStatuses.has(status);
  }

  static getPhoneReportCreationTooltip(
    createdAt: string | Date,
    isAlreadyReported: boolean,
    isAdmin: boolean,
    canAdminCreateReport: boolean,
    status?: TestStatusEnum | null
  ): string | undefined {
    if (isAdmin && !canAdminCreateReport && !isAlreadyReported) {
      return 'Admins can submit phone reports only for their tests. Or impersonate the user to submit phone report from their company.';
    }

    if (isAlreadyReported) return undefined;

    const isTestCreatedWithinHours = CustomUtils.isTestWithinHours(createdAt, 24);
    const isBlocked = this.isPhoneReportBlockedByStatus(status);

    if (isBlocked) {
      return 'Reporting is unavailable because the test number was temporarily offline. This usually means the connection was temporarily lost, not that the number is faulty.';
    }

    if (!isTestCreatedWithinHours) {
      return `Phone number reports are only available within 24 hours of test creation.`;
    }

    return undefined;
  }

  static isReportOutcomeClickable(isAdmin: boolean, phoneReport: PhoneReport): boolean {
    return isAdmin ? true : phoneReport.outcomeResolution !== PhoneReportOutcomeResolutionEnum.UNDER_REVIEW;
  }

  static getReportOutcomeResolutionLabel(resolution?: PhoneReportOutcomeResolutionEnum | null): string | undefined {
    if (!resolution) {
      return;
    }

    return this.reportOutcomeLabels[resolution];
  }

  private static reportOutcomeLabels: Record<PhoneReportOutcomeResolutionEnum, string> = {
    [PhoneReportOutcomeResolutionEnum.UNDER_REVIEW]: 'Under Review',
    [PhoneReportOutcomeResolutionEnum.EXTERNAL_NUMBER]: 'Sender Issue',
    [PhoneReportOutcomeResolutionEnum.GHOST_PROBLEM]: 'Intermittent Issue',
    [PhoneReportOutcomeResolutionEnum.NETWORK_ISSUE]: 'Network Problem',
    [PhoneReportOutcomeResolutionEnum.NO_ISSUE]: 'No Issue Found',
    [PhoneReportOutcomeResolutionEnum.NUMBER_ISSUE]: 'Number Issue',
  };
}
